import '../operations.css';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Table } from '../../components/ui/Table';
import { Modal } from '../../components/ui/Modal';
import { reportsApi } from '../../api/reports';
import { FileBarChart } from 'lucide-react';
import type {
  GeneratedReport,
  ReportFrequency,
  ReportFormat,
  ReportSchedule,
} from '../../api/reports';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function describeSchedule(s: ReportSchedule): string {
  return s.frequency === 'weekly'
    ? `Weekly on ${DAY_NAMES[s.day_of_week] ?? s.day_of_week}`
    : `Monthly on day ${s.day_of_month}`;
}

function formatTime(iso: string | null): string {
  if (!iso) return 'Never';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? 'N/A' : d.toLocaleString();
}

interface ScheduleFormState {
  name: string;
  frequency: ReportFrequency;
  day_of_week: number;
  day_of_month: number;
  recipients: string;
  format: ReportFormat;
}

const emptyForm: ScheduleFormState = {
  name: '',
  frequency: 'weekly',
  day_of_week: 1,
  day_of_month: 1,
  recipients: '',
  format: 'pdf',
};

export function Reports() {
  const queryClient = useQueryClient();
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<ScheduleFormState>(emptyForm);

  const schedulesQuery = useQuery({
    queryKey: ['report-schedules'],
    queryFn: async () => (await reportsApi.listSchedules()).data,
  });

  const generatedQuery = useQuery({
    queryKey: ['generated-reports'],
    queryFn: async () => (await reportsApi.listGenerated()).data,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['report-schedules'] });
    queryClient.invalidateQueries({ queryKey: ['generated-reports'] });
  };

  const createMutation = useMutation({
    mutationFn: () =>
      reportsApi.createSchedule({
        name: form.name,
        frequency: form.frequency,
        day_of_week: form.day_of_week,
        day_of_month: form.day_of_month,
        recipients: form.recipients,
        format: form.format,
        scope: 'all',
      }),
    onSuccess: () => {
      setFormOpen(false);
      setForm(emptyForm);
      invalidate();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => reportsApi.deleteSchedule(id),
    onSuccess: invalidate,
  });

  const runMutation = useMutation({
    mutationFn: (id: number) => reportsApi.runSchedule(id),
    onSuccess: invalidate,
  });

  const scheduleColumns = [
    {
      key: 'name',
      header: 'Name',
      render: (s: ReportSchedule) => <span className="font-medium text-gray-900">{s.name}</span>,
    },
    { key: 'frequency', header: 'Cadence', render: describeSchedule },
    { key: 'format', header: 'Format', render: (s: ReportSchedule) => s.format.toUpperCase() },
    { key: 'next_run_at', header: 'Next Run', render: (s: ReportSchedule) => formatTime(s.next_run_at) },
    { key: 'last_run_at', header: 'Last Run', render: (s: ReportSchedule) => formatTime(s.last_run_at) },
    {
      key: 'actions',
      header: '',
      render: (s: ReportSchedule) => (
        <div className="flex gap-2">
          <Button size="sm" variant="secondary" onClick={() => runMutation.mutate(s.id)}>
            Run now
          </Button>
          <Button size="sm" variant="danger" onClick={() => deleteMutation.mutate(s.id)}>
            Delete
          </Button>
        </div>
      ),
    },
  ];

  const generatedColumns = [
    {
      key: 'name',
      header: 'Report',
      render: (r: GeneratedReport) => <span className="font-medium text-gray-900">{r.name}</span>,
    },
    { key: 'filename', header: 'File' },
    { key: 'format', header: 'Format', render: (r: GeneratedReport) => r.format.toUpperCase() },
    { key: 'created_at', header: 'Generated', render: (r: GeneratedReport) => formatTime(r.created_at) },
    {
      key: 'actions',
      header: '',
      render: (r: GeneratedReport) => (
        <Button size="sm" variant="secondary" onClick={() => reportsApi.download(r)}>
          Download
        </Button>
      ),
    },
  ];

  return (
    <div className="operations-page space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="operations-eyebrow">Behavior intelligence</p>
          <h1 className="text-2xl font-bold text-gray-900">Executive reports</h1>
          <p className="text-gray-500 mt-1">
            Scheduled PDF/CSV digests of campaign performance and repeat offenders
          </p>
        </div>
        <Button onClick={() => setFormOpen(true)}>New schedule</Button>
      </div>

      <section className="report-intro"><FileBarChart size={25} /><div><h2>Keep your program visible</h2><p>Set a reporting cadence for stakeholders, then download the generated evidence below. Each schedule uses your workspace’s campaign data.</p></div></section>
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Schedules</h3>
        {schedulesQuery.isError ? (
          <p role="alert" className="text-red-700">Could not load report schedules.</p>
        ) : (
          <Table
            columns={scheduleColumns}
            data={schedulesQuery.data ?? []}
            keyExtractor={(s) => s.id}
            emptyMessage="No report schedules yet."
          />
        )}
      </Card>

      <Card>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Generated reports</h3>
        {generatedQuery.isError ? (
          <p role="alert" className="text-red-700">Could not load generated reports.</p>
        ) : (
          <Table
            columns={generatedColumns}
            data={generatedQuery.data ?? []}
            keyExtractor={(r) => r.id}
            emptyMessage="No reports generated yet. Use 'Run now' on a schedule to produce one."
          />
        )}
      </Card>

      <Modal isOpen={formOpen} onClose={() => setFormOpen(false)} title="New report schedule">
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            createMutation.mutate();
          }}
        >
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input
              required
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Weekly exec digest"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="report-frequency" className="block text-sm font-medium text-gray-700 mb-1">Frequency</label>
              <select
                id="report-frequency"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                value={form.frequency}
                onChange={(e) => setForm({ ...form, frequency: e.target.value as ReportFrequency })}
              >
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
            {form.frequency === 'weekly' ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Day of week</label>
                <select
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  value={form.day_of_week}
                  onChange={(e) => setForm({ ...form, day_of_week: Number(e.target.value) })}
                >
                  {DAY_NAMES.map((d, i) => (
                    <option key={d} value={i}>{d}</option>
                  ))}
                </select>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Day of month</label>
                <input
                  type="number"
                  min={1}
                  max={31}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  value={form.day_of_month}
                  onChange={(e) => setForm({ ...form, day_of_month: Number(e.target.value) })}
                />
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Format</label>
            <select
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              value={form.format}
              onChange={(e) => setForm({ ...form, format: e.target.value as ReportFormat })}
            >
              <option value="pdf">PDF</option>
              <option value="csv">CSV</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Recipients (comma-separated emails, optional)
            </label>
            <input
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              value={form.recipients}
              onChange={(e) => setForm({ ...form, recipients: e.target.value })}
              placeholder="exec@example.com, soc@example.com"
            />
            <p className="text-xs text-gray-400 mt-1">
              Stored for future email delivery; reports are downloadable in-app today.
            </p>
          </div>
          {createMutation.isError && (
            <p role="alert" className="text-sm text-red-700">Could not create the schedule.</p>
          )}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setFormOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" loading={createMutation.isPending}>
              Create schedule
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
